import { useEffect, useMemo, useState } from 'react';

import { useBooking, useBookings, type Booking } from '@/api/bookings';
import type { RaiseDisputeRequest } from '@/api/disputes';
import { enterKeyScope, focusAfter, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass, selectClass, textAreaClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { emptyDraft, parseAmount, raiseProblems, type DisputeDraft } from './dispute';

/**
 * Writing down that a customer has objected.
 *
 * <p>
 * <strong>The booking has to be found first, and that is the whole reason this is a drawer with a
 * search in it.</strong> Every other entry point into the dispute log needs an id the register
 * supplies, so without one here the feature would be reachable only by already being on the right
 * booking — which is the defect this codebase keeps finding, a thing built with no way to call it.
 * </p>
 *
 * <p>
 * A garment is optional and its absence is the commoner case: at a counter a customer says the
 * deduction is too much without picking out which line they mean, and forcing a choice would put a
 * garment's name on an argument that was never about one.
 * </p>
 */
export function RaiseDisputeDrawer({
  branchId,
  initialBookingId,
  saving,
  onCommit,
  onClose,
}: {
  branchId: number | undefined;
  /** Pre-picked when the drawer was opened from a row that already knows its booking. */
  initialBookingId?: number | null;
  saving: boolean;
  onCommit: (payload: RaiseDisputeRequest) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<DisputeDraft>(() => ({
    ...emptyDraft,
    bookingId: initialBookingId ?? null,
  }));

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  const focusRef = useAutoFocusFirstField<HTMLDivElement>();

  // Debounced, like `CustomerPicker`: the register is paged for thousands and a keystroke per
  // request would put a query behind every letter of a customer's name.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);

    return () => clearTimeout(timer);
  }, [search]);

  const results = useBookings(draft.bookingId === null ? branchId : undefined, {
    search: debounced || undefined,
    pageSize: 8,
  });

  const chosen = useBooking(draft.bookingId ?? undefined);

  const garments = useMemo(
    () => (chosen.data?.wearers ?? []).flatMap((wearer) => wearer.garments),
    [chosen.data],
  );

  const problems = raiseProblems(draft);

  return (
    <div className="fixed inset-0 z-9999 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
        onClick={onClose}
      />

      <div
        ref={focusRef}
        {...enterKeyScope}
        className="relative flex h-full w-full max-w-[560px] flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg"
      >
        <div className="border-b border-border-color p-4">
          <h2 className="text-base font-semibold text-gray-900">Record a dispute</h2>
          <p className="mt-0.5 text-[13px] text-default">
            What the customer says, while both sides still remember it.
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          {draft.bookingId === null ? (
            <Field label="Which booking" hint="Search by number, customer name or mobile." required>
              <input
                type="search"
                className={controlClass}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Field>
          ) : (
            <ChosenBooking
              booking={chosen.data}
              onClear={() => setDraft({ ...draft, bookingId: null, rentalOrderItemId: null })}
            />
          )}

          {draft.bookingId === null && (
            <ul className="list-none space-y-1.5 ps-0">
              {results.isLoading ? (
                <li className="text-[13px] text-default">Looking…</li>
              ) : (results.data?.items ?? []).length === 0 ? (
                <li className="text-[13px] text-default">
                  {debounced
                    ? 'No booking matches that.'
                    : 'Start typing to find the booking this is about.'}
                </li>
              ) : (
                (results.data?.items ?? []).map((booking) => (
                  <li key={booking.id} className="list-none ps-0">
                    <button
                      type="button"
                      className="w-full cursor-pointer rounded-md border border-border-color p-3 text-start hover:bg-light"
                      onClick={(event) => {
                        setDraft({ ...draft, bookingId: booking.id, rentalOrderItemId: null });

                        /*
                          Picking destroys the control focus is in — the search box is replaced by
                          the card naming what was picked — so focus would fall to <body> on the one
                          screen whose argument is that the mouse is never required.
                        */
                        focusAfter(event.currentTarget);
                      }}
                    >
                      <span className="block text-sm font-medium text-gray-900">
                        {booking.customerName}
                      </span>
                      <span className="font-mono-nums mt-0.5 block text-[13px] text-default">
                        {booking.documentNumber || 'no number yet'}
                        {booking.eventDate ? ` · ${formatDate(booking.eventDate)}` : ''}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}

          {draft.bookingId !== null && garments.length > 0 && (
            <Field
              label="About one garment?"
              hint="Leave it on the whole settlement unless the customer named a garment. Most do not."
            >
              <select
                className={selectClass}
                value={draft.rentalOrderItemId ?? ''}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    rentalOrderItemId:
                      event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              >
                <option value="">The settlement as a whole</option>
                {garments.map((garment) => (
                  <option key={garment.id} value={garment.id}>
                    {garment.itemName} · {garment.serialCode}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field
            label="What the customer says"
            hint="Their words, not a summary. This is what the argument is settled from if it reopens."
            required
          >
            <textarea
              className={textAreaClass}
              rows={4}
              value={draft.claim}
              onChange={(event) => setDraft({ ...draft, claim: event.target.value })}
            />
          </Field>

          <Field
            label="Amount in question"
            hint="Optional. Frozen here, so correcting the charge later does not rewrite what was argued about."
          >
            <input
              type="number"
              min={0}
              step="0.01"
              className={controlClass}
              value={draft.disputedAmount}
              // A wheel over a number input rewrites it while the panel is scrolled past — here
              // that would quietly change what the shop is recorded as arguing over.
              onWheel={(event) => event.currentTarget.blur()}
              onChange={(event) => setDraft({ ...draft, disputedAmount: event.target.value })}
            />
          </Field>

          <Notice tone="info" title="This records the argument, not the money">
            Nothing here changes what the customer is charged. If the shop comes down, the charge
            still moves by correcting the inspection — which is what keeps the record of what was
            first claimed.
          </Notice>
        </div>

        <div className="flex justify-end gap-2 border-t border-border-color p-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon="icon icon-flag"
            shortcut="save"
            data-enter-key-commit
            disabled={saving || problems.length > 0}
            title={problems[0]}
            onClick={() => {
              const amount = parseAmount(draft.disputedAmount);

              onCommit({
                bookingId: draft.bookingId as number,
                claim: draft.claim.trim(),
                rentalOrderItemId: draft.rentalOrderItemId,
                // `undefined` would be a parse failure, which `problems` has already blocked.
                disputedAmount: amount ?? null,
              });
            }}
          >
            Record it
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The booking that was picked, with the deduction it is about, so the claim has context beside it. */
function ChosenBooking({
  booking,
  onClear,
}: {
  booking: Booking | undefined;
  onClear: () => void;
}) {
  return (
    <div className="rounded-md border border-border-color p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">
            {booking?.customerName ?? 'Loading…'}
          </div>
          <div className="font-mono-nums mt-0.5 text-[13px] text-default">
            {booking?.documentNumber || 'no number yet'}
          </div>
          {booking && (
            <div className="mt-1 text-[13px] text-default">
              Deposit {formatAmount(booking.deposit.amount)} · held as{' '}
              {booking.deposit.state.toLowerCase()}
            </div>
          )}
        </div>
        <Button icon="icon icon-x" onClick={onClear}>
          Change
        </Button>
      </div>
    </div>
  );
}
